import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  ARCHIVE_EDUCATION_PROGRAM_LEVEL_MUTATION,
  ARCHIVE_EDUCATION_PROGRAM_LEVEL_REFETCH_QUERIES,
  type ArchiveEducationProgramLevelResponse,
  type ArchiveEducationProgramLevelVariables
} from "./mutations";

export type EducationProgramLevelToArchive = {
  levelCode: string;
  levelLabel: string;
};

export default function ArchiveEducationProgramLevelDialog({
  open,
  level,
  pgaId,
  onClose
}: {
  open: boolean;
  level: EducationProgramLevelToArchive | null;
  pgaId: string;
  onClose: () => void;
}) {
  const [archiveError, setArchiveError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setArchiveError(null);
  }, [open]);

  const [archiveLevel, { loading }] = useMutation<
    ArchiveEducationProgramLevelResponse,
    ArchiveEducationProgramLevelVariables
  >(ARCHIVE_EDUCATION_PROGRAM_LEVEL_MUTATION, {
    refetchQueries: ARCHIVE_EDUCATION_PROGRAM_LEVEL_REFETCH_QUERIES,
    awaitRefetchQueries: true
  });

  const handleConfirm = async () => {
    if (!level) return;

    setArchiveError(null);

    try {
      const { data } = await archiveLevel({
        variables: {
          input: {
            pgaId,
            levelCode: level.levelCode
          }
        }
      });

      if (data?.archiveEducationProgramLevel.success) {
        const payload = data.archiveEducationProgramLevel;
        if (payload.code === "SYNC_FAILED") {
          showSnackbar(payload.message, { variant: "warning" });
        } else {
          showSnackbar("Level archived");
        }
        onClose();
      } else {
        setArchiveError(data?.archiveEducationProgramLevel.message ?? "Unable to archive this level");
      }
    } catch {
      setArchiveError("Unable to archive this level");
    }
  };

  const levelName = level ? `${level.levelLabel} (${level.levelCode})` : "this level";

  return (
    <Dialog
      open={open}
      headline="Archive this level?"
      onClosed={onClose}
      disableClose={loading}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={loading} testId="archive-education-program-level-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleConfirm}
            loading={loading}
            disabled={!level}
            testId="archive-education-program-level-confirm"
          >
            Archive
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {archiveError && (
          <Banner variant="error" description={archiveError} inline closable={false} hideSecondaryDismiss />
        )}
        <p className="text-body-medium">
          This moves <strong>{levelName}</strong> and its requirements off the live program so you can start another
          path. Electives completed during this level move with it. You cannot undo this from here.
        </p>
      </div>
    </Dialog>
  );
}
